import { useState, useEffect, useCallback, useRef } from 'react'
import { Circle } from '@react-google-maps/api'
import { useAppDispatch, useAppSelector } from 'redux/hooks'
import {
  MIN_RADIUS,
  MAX_RADIUS,
  updateSearchCircleRadius,
  updateSearchCircleCenter,
} from 'redux/features/map/slice'
import { makeSelectSearchCircle } from 'redux/features/map/selectors'
import { fetchWithCheckAuthorization } from 'utils/fetchWithAuthorization'
import LatLng from 'interfaces/LatLng'
import ReactDOM from 'react-dom'
import { Alert } from 'react-bootstrap'
import TripleGrow from 'components/Loading/TripleGrow'
import s from './SearchCircle.module.scss'

const SearchCircle = (): JSX.Element | null => {
  const searchCircle = useAppSelector(makeSelectSearchCircle)
  const dispatch = useAppDispatch()
  const [circle, setCircle] = useState<google.maps.Circle>()
  const [authorized, setAuthorized] = useState(false)
  const [loading, setLoading] = useState(false)
  const [hover, setHover] = useState(false)
  const mapNode = useRef<HTMLDivElement>(
    document.querySelector('#geociblage-map')
  )

  const isCircleAuthorize = useCallback(
    async (center: LatLng | undefined = searchCircle?.center) => {
      try {
        if (!center) return
        setLoading(true)
        const response = await fetchWithCheckAuthorization(
          `/api/agent/codes-postaux/point-zone-autorise?longitude=${center.lng}&latitude=${center.lat}`
        )
        if (!response.ok)
          throw Error(
            'Could not verify that the center of the circle is in the agent authorized bounds.'
          )
        const json = await response.json()
        setAuthorized(json.response)
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    },
    [searchCircle?.center]
  )

  const handleRadiusChange = () => {
    if (!circle) return
    dispatch(updateSearchCircleRadius(Math.round(circle.getRadius())))
  }

  const handleCenterChange = () => {
    if (!circle) return
    const center = circle.getCenter()?.toJSON() as LatLng
    dispatch(updateSearchCircleCenter(center))
    isCircleAuthorize(center)
  }

  // To handle min-max radius size when resizing directly from the shape
  useEffect(() => {
    if (searchCircle && searchCircle.radius > MAX_RADIUS)
      dispatch(updateSearchCircleRadius(MAX_RADIUS))
    if (searchCircle && searchCircle.radius < MIN_RADIUS)
      dispatch(updateSearchCircleRadius(MIN_RADIUS))
  }, [searchCircle?.radius, searchCircle, dispatch])

  useEffect(() => {
    isCircleAuthorize()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!searchCircle) return null

  return (
    <>
      <Circle
        center={searchCircle.center}
        radius={searchCircle.radius}
        onLoad={(c) => setCircle(c)}
        onUnmount={() => setCircle(undefined)}
        onMouseOver={() => setHover(true)}
        onMouseOut={() => setHover(false)}
        onRadiusChanged={handleRadiusChange}
        onDragEnd={handleCenterChange}
        options={{
          fillColor: authorized ? '#FF945F' : '#000000',
          strokeColor: hover ? '#a6430e' : 'transparent',
        }}
        draggable
        editable={hover}
      />
      {mapNode.current &&
        ReactDOM.createPortal(
          <>
            {!authorized && !loading && (
              <Alert className={s.unauthorized} variant="danger">
                Le centre de votre zone de recherche doit être placé sur votre
                zone de prospection.
              </Alert>
            )}
            {loading && (
              <Alert className={s.loading} variant="light">
                <TripleGrow />
              </Alert>
            )}
          </>,
          mapNode.current
        )}
    </>
  )
}

export default SearchCircle
